/**
 * A part's characters flying across the page, from where it was to where it
 * goes.
 *
 * A copy of the characters is drawn over the page at the start and moved to
 * the end, scaled evenly so a part arriving in a large box arrives large, and
 * removed. The question card flies a part from its tile into the sum, and the
 * marked card from the sum into the square, where a part that does not belong
 * falls away instead of landing (`./fuxi-assembled.ts`).
 *
 * **A reader who asked for less motion sees nothing fly.** Every flight
 * resolves at once for them, and the element at the end is shown as it is.
 */

/** Whether the reader asked for less motion, or the page cannot say. */
export function wantsStillness(page: Document): boolean {
  return (
    page.defaultView?.matchMedia?.("(prefers-reduced-motion: reduce)")
      .matches ?? true
  );
}

/** Wait this long, on the page's own clock. */
export function after(page: Document, milliseconds: number): Promise<void> {
  return new Promise((settled) => {
    (page.defaultView ?? globalThis).setTimeout(settled, milliseconds);
  });
}

/** The copy that flies, drawn over the starting element. */
function ghostOver(from: Element): HTMLElement {
  const page = from.ownerDocument;
  const start = from.getBoundingClientRect();
  const ghost = page.createElement("span");
  const style = page.defaultView?.getComputedStyle(from);

  ghost.className = "cb-fuxi-zuzi-ghost";
  ghost.textContent = from.textContent;
  ghost.lang = "zh-Hans";
  ghost.style.left = `${String(start.left)}px`;
  ghost.style.top = `${String(start.top)}px`;
  ghost.style.width = `${String(start.width)}px`;
  ghost.style.height = `${String(start.height)}px`;
  ghost.style.fontSize = style?.fontSize ?? "";
  ghost.style.color = style?.color ?? "";
  page.body.append(ghost);

  return ghost;
}

/** The move from one box to another, as a transform from the first. */
function moveTo(start: DOMRect, end: DOMRect): string {
  const scale = Math.min(end.width / start.width, end.height / start.height);
  const x = end.left + end.width / 2 - (start.left + (start.width * scale) / 2);
  const y = end.top + end.height / 2 - (start.top + (start.height * scale) / 2);

  return `translate(${String(x)}px, ${String(y)}px) scale(${String(scale)})`;
}

/**
 * Fly a copy of one element's characters to another, or to a box.
 *
 * @param properties - What flies, where to, how long it takes, and whether it
 * falls away on arriving instead of landing.
 * @returns When the flight is over and the copy is gone.
 */
export async function flyText(properties: {
  readonly from: Element;
  readonly to: Element | DOMRect;
  readonly duration: number;
  readonly fall?: boolean;
}): Promise<void> {
  const { from } = properties;
  const page = from.ownerDocument;
  const start = from.getBoundingClientRect();
  const end =
    properties.to instanceof Element
      ? properties.to.getBoundingClientRect()
      : properties.to;

  if (
    wantsStillness(page) ||
    typeof page.body.animate !== "function" ||
    start.width === 0 ||
    end.width === 0
  ) {
    return;
  }

  const ghost = ghostOver(from);
  const landed = moveTo(start, end);

  try {
    await ghost.animate(
      [{ transform: "translate(0, 0) scale(1)" }, { transform: landed }],
      {
        duration: properties.duration,
        easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
        fill: "forwards",
      },
    ).finished;

    if (properties.fall === true) {
      await ghost.animate(
        [
          { transform: landed, opacity: 1 },
          {
            transform: `${landed} translate(0, -6%) rotate(-4deg)`,
            opacity: 1,
            offset: 0.25,
          },
          {
            transform: `${landed} translate(12%, 70%) rotate(24deg)`,
            opacity: 0,
          },
        ],
        {
          duration: 620,
          easing: "cubic-bezier(0.5, 0, 0.9, 0.6)",
          fill: "forwards",
        },
      ).finished;
    }
  } catch {
    // A flight cancelled by the card going is over either way.
  } finally {
    ghost.remove();
  }
}
